// Framework-agnostic helpers for the "People you've worked with" suggestion list
// in the Invite Collaborators UI (src/components/collaborators/invite.vue).
//
// Kept out of the component (and out of the Vuex store) so the logic is unit
// testable with an injected resolver — see test/unit/specs/contacts.spec.js.
// This module imports nothing (no firebase / no @/ aliases) on purpose: the
// caller passes in `db`-backed data and a `userByUID` resolver.

// Normalize an email for dedupe / comparison. Everything keys off lowercased email.
export function normEmail(email) {
    return (email || '').toString().trim().toLowerCase();
}

// Resolve a set/array of uids to { uid, email, username } via the provided
// resolver (getters.userByUID). Caches by uid in the injected Map so repeat
// opens within a session don't re-read. Drops docs with no email — email is
// load-bearing, we can't invite without one. A null cache disables caching.
export async function resolveContacts(resolver, uids, cache) {
    const list = Array.from(uids || []);
    const out = [];
    await Promise.all(list.map(async (uid) => {
        let doc;
        if (cache && cache.has(uid)) {
            doc = cache.get(uid);
        } else {
            let user = null;
            try {
                user = await resolver(uid);
            } catch (e) {
                user = null;
            }
            doc = user ? { uid, email: user.email, username: user.username } : null;
            if (cache) {
                cache.set(uid, doc);
            }
        }
        if (doc && doc.email) {
            out.push(doc);
        }
    }));
    return out;
}

// Merge the three sources into one deduped list of { uid, name, email, source }.
// Dedupe by uid; the first source to add a uid wins its position and source
// label, but a later source can fill in a missing name/email. Pass sources in
// display-priority order (collaborators first). No `role` is ever carried — a
// project-specific role/permission must not leak across projects.
export function mergeSuggestions({ collaborators, inviters, affiliates } = {}) {
    const byUid = new Map();
    const push = arr => (arr || []).forEach((e) => {
        if (!e || !e.uid) return;
        const prev = byUid.get(e.uid);
        if (!prev) {
            byUid.set(e.uid, {
                uid: e.uid,
                name: e.name || '',
                email: e.email || '',
                source: e.source
            });
        } else {
            if (!prev.email && e.email) prev.email = e.email;
            if (!prev.name && e.name) prev.name = e.name;
        }
    });
    push(collaborators);
    push(inviters);
    push(affiliates);
    return Array.from(byUid.values());
}

// Build the sets of uids + lowercased emails to exclude from suggestions:
// self, everyone already on the project (map in the editor, array in the
// create-project wizard — hence shape-agnostic), the project owner, and anyone
// with a pending invite. Pure so the wizard/editor divergence is unit tested.
export function buildExclusion({ project, user, pending } = {}) {
    const uids = new Set();
    const emails = new Set();
    const addEmail = (e) => { const n = normEmail(e); if (n) emails.add(n); };

    if (user) {
        if (user.uid) uids.add(user.uid);
        addEmail(user.email);
    }
    const collabs = project && project.collaborators;
    const list = Array.isArray(collabs) ? collabs : Object.values(collabs || {});
    list.forEach((c) => {
        if (c && c.uid) uids.add(c.uid);
        if (c) addEmail(c.email);
    });
    if (project && project.ownerUID) uids.add(project.ownerUID);
    (pending || []).forEach((p) => { if (p) addEmail(p.email); });

    return { uids, emails };
}

// Apply exclusion + live text filtering, capped at `limit`. `tokens` are the
// current Email/Name field values; a suggestion must contain ALL non-empty
// tokens somewhere in "name email" (case-insensitive), so typing in either
// field narrows the list.
export function filterSuggestions(suggestions, { exclusion, tokens, limit = 8 } = {}) {
    const { uids, emails } = exclusion || { uids: new Set(), emails: new Set() };
    const toks = (tokens || []).map(t => (t || '').toString().trim().toLowerCase()).filter(Boolean);
    const match = (s) => {
        if (s.uid && uids.has(s.uid)) return false;
        const email = normEmail(s.email);
        if (email && emails.has(email)) return false;
        if (toks.length) {
            const hay = `${s.name || ''} ${s.email || ''}`.toLowerCase();
            return toks.every(t => hay.includes(t));
        }
        return true;
    };
    return (suggestions || []).filter(match).slice(0, limit);
}



// WEBPACK FOOTER //
// ./src/lib/contacts.js